import { useQuery } from '@tanstack/react-query';
import { BookOpen, Clock, History, RotateCcw, Server as ServerIcon, SlidersHorizontal } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Panel, Pill, SectionHeader, Skeleton } from '@/components/surface/ui';
import { AUTOMATION_CATALOG_KEY, RUNS_KEY, automationApi } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import { LaunchPanel } from '@/modules/automation/LaunchPanel';
import { ParameterFields } from '@/modules/automation/ParameterFields';
import { SAFE_STRATEGY, changedValues, initialSelection, initialValues, isCompleteSelection, parameterErrors } from '@/modules/automation/parameters';
import { RecentRuns } from '@/modules/automation/RecentRuns';
import { categoryLabel } from '@/modules/automation/status';
import { TargetPicker } from '@/modules/automation/TargetPicker';
import { PageHeader, TechnicalDetails } from '@/modules/automation/ui';
import type { ParameterValue, PlaybookSummary, RunMode, RunStrategy, TargetPreview, TargetSelection } from '@/types/api';

export function PlaybookRunPage() {
  const { key = '' } = useParams();
  const catalog = useQuery({ queryKey: AUTOMATION_CATALOG_KEY, queryFn: () => automationApi.catalog(), staleTime: 60_000 });
  const playbook = catalog.data?.playbooks.find((item) => item.key === key);

  if (catalog.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (catalog.isError) {
    return <p className="text-sm text-danger">The playbook could not be loaded: {getErrorMessage(catalog.error)}</p>;
  }

  if (playbook === undefined) {
    return (
      <div className="space-y-4">
        <PageHeader title="Playbook not found" back={{ to: '/automation/playbooks', label: 'Playbooks' }} />
        <p className="text-sm text-muted-foreground">There is no playbook called “{key}”.</p>
      </div>
    );
  }

  // Keyed by playbook, so moving between playbooks starts each form fresh.
  return <PlaybookForm key={playbook.key} playbook={playbook} />;
}

function PlaybookForm({ playbook }: { playbook: PlaybookSummary }) {
  const [values, setValues] = useState<Record<string, ParameterValue>>(() => initialValues(playbook.parameters));
  const [searchParams] = useSearchParams();
  const [selection, setSelection] = useState<TargetSelection>(() => initialSelection(searchParams));
  const [strategy, setStrategy] = useState<RunStrategy>(SAFE_STRATEGY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<TargetPreview | null>(null);

  const onPreview = useCallback((next: TargetPreview | null) => setPreview(next), []);

  const serverCount = preview !== null && preview.problems.length === 0 ? preview.servers.length : null;

  const blockedReason = useMemo(() => {
    if (!isCompleteSelection(selection)) return 'Choose the servers to run on.';
    if (preview !== null && preview.problems.length > 0) return preview.problems[0] ?? null;
    return null;
  }, [selection, preview]);

  const build = (mode: RunMode) => ({
    kind: 'playbook' as const,
    subject: playbook.key,
    parameters: changedValues(playbook.parameters, values),
    mode,
    strategy,
    targets: selection,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ to: '/automation/playbooks', label: 'Playbooks' }}
        eyebrow={
          <>
            <Pill tone="primary" icon={BookOpen}>
              Playbook
            </Pill>
            <Pill tone="muted">{categoryLabel[playbook.category]}</Pill>
            <Pill tone="muted">Version {playbook.version}</Pill>
          </>
        }
        title={playbook.name}
        description={playbook.summary}
        actions={
          <Link
            to={`/help/playbooks/${playbook.key}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/40"
          >
            <BookOpen className="size-4" aria-hidden />
            Guide and options
          </Link>
        }
      />

      <SubscriptionNotice />

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-6">
          <Panel>
            <SectionHeader icon={BookOpen} title="What it does" />
            <div className="space-y-3 px-5 pb-5 text-sm leading-6">
              <p>{playbook.description}</p>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">Supported systems</dt>
                  <dd>{playbook.supported_systems}</dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1 text-muted-foreground">
                    <RotateCcw className="size-3" aria-hidden /> Temporary failures
                  </dt>
                  <dd>{playbook.retries === 0 ? 'Not retried' : `Retried up to ${playbook.retries} time${playbook.retries === 1 ? '' : 's'}`}</dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="size-3" aria-hidden /> Time limit per server
                  </dt>
                  <dd>{Math.round(playbook.timeout_seconds / 60)} minutes</dd>
                </div>
              </dl>
            </div>
            <TechnicalDetails details={playbook.details} />
          </Panel>

          {playbook.parameters.length > 0 && (
            <Panel>
              <SectionHeader icon={SlidersHorizontal} title="Choices" />
              <div className="px-5 pb-5">
                <ParameterFields
                  parameters={playbook.parameters}
                  values={values}
                  onChange={(parameter, value) => setValues((current) => ({ ...current, [parameter]: value }))}
                  errors={parameterErrors(errors)}
                />
              </div>
            </Panel>
          )}

          <Panel>
            <SectionHeader icon={ServerIcon} title="Servers" description="The preview lists what the platform already expects to go wrong on each server." />
            <div className="px-5 pb-5">
              <TargetPicker
                value={selection}
                onChange={setSelection}
                kind="playbook"
                subject={playbook.key}
                error={errors['targets.values'] ?? errors['targets.mode']}
                onPreview={onPreview}
              />
            </div>
          </Panel>
        </div>

        <div className="space-y-6 xl:sticky xl:top-20">
          <LaunchPanel
            strategy={strategy}
            onStrategyChange={setStrategy}
            serverCount={serverCount}
            allowDryRun
            build={build}
            onFieldErrors={setErrors}
            blockedReason={blockedReason}
          />

          <Panel>
            <SectionHeader
              icon={History}
              title="Recent runs"
              actions={
                <Link to={`/automation/runs?kind=playbook&subject=${playbook.key}`} className="text-xs font-medium text-primary hover:underline">
                  All
                </Link>
              }
            />
            <RecentRuns queryKey={[...RUNS_KEY, 'playbook', playbook.key]} filters={{ kind: 'playbook', subject: playbook.key, limit: 5 }} />
          </Panel>
        </div>
      </div>
    </div>
  );
}
